/* @flow */

import * as React from 'react';
import {components, type SingleValueProps, type OptionProps} from 'react-select';

import type {Assignable} from 'nutshell-core/types';

import {Avatar} from '../avatar';
import {ContactAssigneeIcon} from '../icon';
import {SelectPickerGeneric} from '../select-picker-generic';

import './assignee-select.module.css';

export type CustomOption = {|id: string, name: string|};

type Props = {|
    inputId?: string,
    options: $ReadOnlyArray<Assignable>,
    customOptions?: CustomOption[],
    value: ?string, // ID
    onChange: (id: ?string) => void,
    isClearable?: boolean,
    placeholder?: string,
    isDisabled?: boolean,
    withControlIcon?: boolean,
    hideDividerWhenEmpty?: boolean,
    minHeight?: number,
    menuPlacement?: 'auto' | 'bottom' | 'top',
    showTooltip?: boolean,
|};

type Option = {
    value: Assignable | CustomOption,
    label: string,
};

type AssigneeSingleValueProps = {
    ...SingleValueProps,
    showTooltip?: boolean,
};

/**
 * Use this to render a dropdown to choose a single assignee.
 */
export const AssigneeSelect = (props: Props) => {
    const {
        inputId,
        options,
        customOptions,
        value,
        onChange,
        isClearable = true,
        placeholder = 'Choose an assignee…',
        isDisabled,
        withControlIcon,
        hideDividerWhenEmpty,
        minHeight,
        menuPlacement = 'auto',
        showTooltip,
    } = props;

    let optionsList: Option[] = options.map((item) => ({
        value: item,
        label: item.name,
        isDisabled: item.isEnabled === false,
        isLoggedInUser: item.isLoggedInUser,
    }));

    if (customOptions) {
        optionsList = customOptions
            .map((item) => ({
                value: item,
                label: item.name,
            }))
            .concat(optionsList);
    }

    const selectedValue = optionsList.find((opt) => opt.value.id === value);

    let controlIcon;
    if (withControlIcon) {
        // When selected value is a user, we show the avatar - so no need for another icon
        if (!selectedValue || (selectedValue && selectedValue.value.type === 'teams')) {
            controlIcon = ContactAssigneeIcon;
        }
    }

    const handleChange = (newOption: Option) => {
        onChange(newOption.value.id);
    };

    const handleClear = () => {
        onChange(null);
    };

    const isOptionSelected = (option: Option, selectedValues: Option[]) => {
        return option.value === selectedValues[0];
    };

    return (
        <SelectPickerGeneric
            inputId={inputId}
            backspaceRemovesValue={false}
            openMenuOnFocus={true}
            menuShouldScrollIntoView={true}
            value={selectedValue && {...selectedValue.value, showTooltip: showTooltip}}
            onChange={handleChange}
            onClear={isClearable ? handleClear : undefined}
            options={optionsList}
            isDisabled={isDisabled}
            blurInputOnSelect={true}
            isOptionSelected={isOptionSelected}
            placeholder={placeholder}
            styles={{
                menu: (base) => ({...base, zIndex: 5}),
            }}
            components={{
                Option: OptionComponent,
                SingleValue: SingleValueComponent,
            }}
            controlIcon={controlIcon}
            hideDividerWhenEmpty={hideDividerWhenEmpty}
            minHeight={minHeight}
            menuPlacement={menuPlacement}
        />
    );
};

const SingleValueComponent = (props: AssigneeSingleValueProps) => {
    return (
        <components.SingleValue {...props}>
            <AssignableComponent assignable={props.data} showTooltip={props.data.showTooltip} />
        </components.SingleValue>
    );
};

const OptionComponent = (props: OptionProps) => {
    return (
        <components.Option {...props}>
            <AssignableComponent
                assignable={props.data.value}
                isLoggedInUser={props.data.isLoggedInUser}
            />
        </components.Option>
    );
};

type AssignableComponentProps = {
    assignable: Assignable | CustomOption,
    isLoggedInUser?: boolean,
    showTooltip?: boolean,
};

const AssignableComponent = ({
    assignable,
    isLoggedInUser,
    showTooltip,
}: AssignableComponentProps) => (
    <div styleName='option-container' data-tip={showTooltip ? assignable.name : undefined}>
        {assignable.type && assignable.type === 'users' ? (
            <Avatar
                avatarUrl={assignable.avatarUrl}
                initials={assignable.initials}
                size={20}
                type={assignable.type}
                isDisabled={!assignable.isEnabled}
            />
        ) : null}
        <div
            styleName='option-title'
            className={
                assignable.type === 'users' && !assignable.isEnabled
                    ? 'light-text strikethrough'
                    : undefined
            }
        >
            {assignable.name}
            {isLoggedInUser && <span styleName='you-text'> (You)</span>}
        </div>
    </div>
);
